import React, { useState, useMemo } from 'react';
import { useTheme } from '../../../utils/ThemeSwitcher';
import {
  Lightbulb,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Info,
  MessageSquare,
  Headphones,
  Ear,
  Ban,
  Copy as CopyIcon,
  Check,
} from 'lucide-react';

/**
 * CoachingPanel — Action Suggestions card for detector dashboards.
 *
 * Renders the natural-language coaching suggestions produced by the
 * `coaching_suggestions.py` backend module. Each suggestion is a
 * collapsible card with:
 *   - Title + severity badge
 *   - One-line summary (always visible)
 *   - Conversation opener (script the user can copy)
 *   - Talking points (what to cover)
 *   - Listen-for branches (decision tree based on response)
 *   - Don't-do warnings (red-bordered)
 *
 * Filters by `detectorSource` so each detector's Summary tab shows only
 * its own suggestions plus any cross-detector signals.
 */
const CoachingPanel = ({ suggestions = [], detectorSource = null, isMobile = false }) => {
  const { isDarkMode } = useTheme();
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [copiedId, setCopiedId]       = useState(null);

  // Filter:
  //   - own-detector suggestions: always shown
  //   - cross-detector suggestions: shown only if this detector is in
  //     evidence.detectors_involved (legacy suggestions without that
  //     evidence array fall back to the old "show on every tab" behavior)
  const visible = useMemo(() => {
    if (!detectorSource) return suggestions;
    return suggestions.filter(s => {
      if (s.detector_source === detectorSource) return true;
      if (s.detector_source === 'cross_detector') {
        const involved = s.evidence?.detectors_involved;
        if (!Array.isArray(involved) || involved.length === 0) {
          // legacy suggestion — keep old permissive behavior
          return true;
        }
        return involved.includes(detectorSource);
      }
      return false;
    });
  }, [suggestions, detectorSource]);

  if (!visible.length) return null;

  const colors = {
    cardBg:        isDarkMode ? '#141516' : '#FFFFFF',
    cardBgHover:   isDarkMode ? '#1A1B1C' : '#FAFAFA',
    border:        isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    borderLight:   isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)',
    text:          isDarkMode ? '#F0EDE4' : '#1F2937',
    textSecondary: isDarkMode ? '#A8A49A' : '#6B7280',
    textMuted:     isDarkMode ? '#6B6862' : '#9CA3AF',
    high:          '#DC2626',
    medium:        '#D97706',
    low:           '#0369A1',
    info:          '#059669',
    accent:        isDarkMode ? '#FBBF24' : '#D97706',
    quoteBg:       isDarkMode ? 'rgba(217,119,6,0.08)' : 'rgba(217,119,6,0.06)',
    quoteBorder:   isDarkMode ? 'rgba(217,119,6,0.25)' : 'rgba(217,119,6,0.18)',
    dontBg:        isDarkMode ? 'rgba(220,38,38,0.06)' : 'rgba(220,38,38,0.04)',
    dontBorder:    isDarkMode ? 'rgba(220,38,38,0.20)' : 'rgba(220,38,38,0.18)',
  };

  const sevIcon = (sev) => sev === 'high'   ? AlertTriangle
                         : sev === 'medium' ? AlertCircle
                         : sev === 'low'    ? Info
                                            : CheckCircle;

  const sevColor = (sev) => sev === 'high'   ? colors.high
                          : sev === 'medium' ? colors.medium
                          : sev === 'low'    ? colors.low
                                             : colors.info;

  const sevLabel = (sev) => sev === 'high'   ? 'High priority'
                          : sev === 'medium' ? 'Worth a conversation'
                          : sev === 'low'    ? 'Low priority'
                                             : 'Healthy';

  const toggle = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const copyOpener = (e, id, text) => {
    e.stopPropagation();
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1800);
    }).catch(() => {});
  };

  return (
    <div style={{ marginTop: '24px', marginBottom: '24px' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        marginBottom: '12px',
      }}>
        <Lightbulb size={14} style={{ color: colors.accent }} />
        <div style={{
          fontSize: '11px', textTransform: 'uppercase',
          letterSpacing: '0.12em', color: colors.textMuted, fontWeight: 500,
        }}>
          action suggestions
        </div>
        <div style={{
          marginLeft: 'auto', fontSize: '11px', color: colors.textMuted,
        }}>
          {visible.length} {visible.length === 1 ? 'suggestion' : 'suggestions'}
        </div>
      </div>

      {/* Suggestion cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {visible.map((s) => {
          const expanded = expandedIds.has(s.id);
          const SevIcon  = sevIcon(s.severity);
          const sevCol   = sevColor(s.severity);

          return (
            <div
              key={s.id}
              onClick={() => toggle(s.id)}
              style={{
                position: 'relative',
                backgroundColor: colors.cardBg,
                border: `1px solid ${colors.border}`,
                borderRadius: '10px',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.cardBgHover}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.cardBg}
            >
              {/* Severity corner brackets — top-left + bottom-right */}
              <svg width="34" height="34" viewBox="0 0 34 34" style={{
                position: 'absolute', top: 0, left: 0,
                pointerEvents: 'none', overflow: 'visible', zIndex: 1,
              }}>
                <path d="M 32 0 L 10 0 A 10 10 0 0 0 0 10 L 0 32"
                      stroke={sevCol} strokeWidth="2.5" fill="none"
                      strokeLinecap="round" opacity={0.9} />
              </svg>
              <svg width="34" height="34" viewBox="0 0 34 34" style={{
                position: 'absolute', bottom: 0, right: 0,
                pointerEvents: 'none', overflow: 'visible', zIndex: 1,
              }}>
                <path d="M 2 34 L 24 34 A 10 10 0 0 0 34 24 L 34 2"
                      stroke={sevCol} strokeWidth="2.5" fill="none"
                      strokeLinecap="round" opacity={0.9} />
              </svg>

              {/* Card header */}
              <div style={{
                padding: '14px 16px',
                display: 'flex', alignItems: 'flex-start', gap: '12px',
              }}>
                <SevIcon size={16} style={{ color: sevCol, flexShrink: 0, marginTop: '2px' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                    gap: '8px', marginBottom: '4px',
                  }}>
                    <div style={{
                      fontSize: '14px', fontWeight: 600, color: colors.text,
                      lineHeight: 1.3,
                    }}>
                      {s.title}
                    </div>
                    <span style={{
                      fontSize: '10px', textTransform: 'uppercase',
                      letterSpacing: '0.05em', fontWeight: 600,
                      color: sevCol,
                      background: `${sevCol}15`,
                      padding: '2px 8px', borderRadius: '999px',
                    }}>
                      {sevLabel(s.severity)}
                    </span>
                    {s.detector_source === 'cross_detector' && (
                      <span style={{
                        fontSize: '10px', textTransform: 'uppercase',
                        letterSpacing: '0.05em', fontWeight: 500,
                        color: colors.textMuted,
                        background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                        padding: '2px 8px', borderRadius: '999px',
                      }}>
                        cross-detector
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: '13px', color: colors.textSecondary, lineHeight: 1.5,
                  }}>
                    {s.summary}
                  </div>
                </div>
                <div style={{
                  color: colors.textMuted, marginTop: '2px', flexShrink: 0,
                }}>
                  {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>
              </div>

              {/* Card body — only when expanded */}
              {expanded && (
                <div style={{
                  borderTop: `1px solid ${colors.borderLight}`,
                  padding: '14px 16px 16px 44px',  // align with title indent
                }}>
                  {/* Conversation opener */}
                  {s.opening_line && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{
                        ...sectionLabelStyle(colors),
                        display: 'flex', alignItems: 'center', gap: '6px',
                      }}>
                        <MessageSquare size={11} style={{ color: colors.accent }} />
                        opener
                      </div>
                      <div
                        onClick={(e) => copyOpener(e, s.id, s.opening_line)}
                        style={{
                          padding: '12px 14px',
                          background: colors.quoteBg,
                          border: `1px solid ${colors.quoteBorder}`,
                          borderRadius: '8px',
                          fontSize: '13.5px',
                          color: colors.text,
                          fontStyle: 'italic',
                          lineHeight: 1.55,
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'background 0.15s ease',
                        }}
                      >
                        {s.opening_line}
                        <div style={{
                          position: 'absolute', top: '8px', right: '8px',
                          display: 'flex', alignItems: 'center', gap: '4px',
                          fontSize: '10px', color: colors.textMuted,
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                        }}>
                          {copiedId === s.id
                            ? <><Check size={11} style={{ color: colors.info }} /> copied</>
                            : <><CopyIcon size={11} /> copy</>}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Talking points */}
                  {s.talking_points && s.talking_points.length > 0 && (
                    <div style={{ marginBottom: '14px' }}>
                      <div style={sectionLabelStyle(colors)}>talking points</div>
                      <ul style={listStyle}>
                        {s.talking_points.map((tp, i) => (
                          <li key={i} style={liStyle(colors)}>
                            <span style={bulletStyle(colors.accent)}>•</span>
                            <span>{tp}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Listen-for / decision tree */}
                  {s.listen_for && s.listen_for.length > 0 && (
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{
                        ...sectionLabelStyle(colors),
                        display: 'flex', alignItems: 'center', gap: '6px',
                      }}>
                        <Ear size={11} style={{ color: colors.textSecondary }} />
                        listen for
                      </div>
                      <ul style={listStyle}>
                        {s.listen_for.map((lf, i) => (
                          <li key={i} style={liStyle(colors)}>
                            <span style={bulletStyle(colors.textMuted)}>→</span>
                            <span>{lf}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Don't-do warnings */}
                  {s.do_not && s.do_not.length > 0 && (
                    <div style={{
                      padding: '10px 12px',
                      background: colors.dontBg,
                      border: `1px solid ${colors.dontBorder}`,
                      borderRadius: '8px',
                    }}>
                      <div style={{
                        ...sectionLabelStyle(colors),
                        color: colors.high,
                        display: 'flex', alignItems: 'center', gap: '6px',
                        marginBottom: '6px',
                      }}>
                        <Ban size={11} /> don't
                      </div>
                      <ul style={{ ...listStyle, marginBottom: 0 }}>
                        {s.do_not.map((dn, i) => (
                          <li key={i} style={{ ...liStyle(colors), color: colors.text }}>
                            <span style={bulletStyle(colors.high)}>×</span>
                            <span>{dn}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------- inline style helpers
const sectionLabelStyle = (colors) => ({
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  fontWeight: 600,
  color: colors.textMuted,
  marginBottom: '6px',
});

const listStyle = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};

const liStyle = (colors) => ({
  display: 'flex',
  gap: '8px',
  fontSize: '13px',
  color: colors.textSecondary,
  lineHeight: 1.55,
});

const bulletStyle = (color) => ({
  flexShrink: 0,
  width: '12px',
  color: color,
  fontWeight: 600,
  textAlign: 'center',
});

export default CoachingPanel;